import {
  ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE,
  clearEnvironmentStripTourRequest,
  claimOnboardingSurface,
  consumeEnvironmentStripTourRequest,
  getEnvironmentStripTourRequest,
  handoverOnboardingSurface,
  isNoticeDismissedForPage,
  subscribeEnvironmentStripTourRequest,
  useActiveOnboardingSurface,
} from '@github-ui/copilot-chat-helpers/copilot-onboarding'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {CopilotOnboardingPopover} from '@github-ui/copilot-shared-ui/components/onboarding/CopilotOnboardingPopover'
import {dismissNotice} from '@github-ui/copilot-shared-ui/components/onboarding/notice-dismissal'
import {
  isVisibleOnboardingAnchor,
  useOnboardingAnchor,
  type OnboardingAnchorId,
} from '@github-ui/copilot-shared-ui/components/onboarding/use-onboarding-anchor'
import {useNoticeDismissal} from '@github-ui/copilot-shared-ui/components/onboarding/use-notice-dismissal'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useCallback, useEffect, useRef, useState, useSyncExternalStore, type RefObject} from 'react'

interface EnvironmentStripTourProps {
  scope: Element | null
  portalSlots: Partial<Record<OnboardingAnchorId, HTMLDivElement>>
  disabled: boolean
  submitting: boolean
  chatOwner: boolean
  chatPickerReady: boolean
  chatPickerFailed?: boolean
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
  onVisibilityChange: (visible: boolean) => void
}

interface TourState {
  steps: OnboardingAnchorId[]
  current: OnboardingAnchorId
  focusRequested: boolean
  focusKey: number
}

const serverRequest = () => null

export function isEnvironmentStripTourEnabled() {
  return copilotFeatureFlags.oneCopilotChat && copilotFeatureFlags.oneCopilotEnvironmentStripTour
}

export function EnvironmentStripTour(props: EnvironmentStripTourProps) {
  const {isBrowser} = useRuntimeEnvironment()
  if (!isBrowser) return null
  return <ActiveEnvironmentStripTour {...props} />
}

function ActiveEnvironmentStripTour({
  scope,
  portalSlots,
  disabled,
  submitting,
  chatOwner,
  chatPickerReady,
  chatPickerFailed = false,
  textAreaRef,
  onVisibilityChange,
}: EnvironmentStripTourProps) {
  const ignoreDismiss = copilotFeatureFlags.oneCopilotEnvironmentStripTourIgnoreDismiss
  const dismissal = useNoticeDismissal(ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE, {enabled: !ignoreDismiss})
  const request = useSyncExternalStore(
    subscribeEnvironmentStripTourRequest,
    getEnvironmentStripTourRequest,
    serverRequest,
  )
  const activeSurface = useActiveOnboardingSurface()
  const chat = useOnboardingAnchor('chat', scope)
  const owner = useOnboardingAnchor('owner', scope)
  const environment = useOnboardingAnchor('environment', scope)
  const [tour, setTour] = useState<TourState | null>(null)
  const releaseRef = useRef<(() => void) | null>(null)
  const dismissedRef = useRef(false)
  const tourFrameRef = useRef<number | null>(null)
  const focusInTourRef = useRef(false)
  const getEscapeScope = useCallback(() => scope?.parentElement ?? null, [scope])

  const close = useCallback(() => {
    if (dismissedRef.current) return
    dismissedRef.current = true
    onVisibilityChange(false)
    releaseRef.current?.()
    releaseRef.current = null
    setTour(null)
    if (!(submitting && isNoticeDismissedForPage(ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE))) {
      void dismissNotice(ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE)
    }
  }, [onVisibilityChange, submitting])

  useEffect(
    () => () => {
      releaseRef.current?.()
      if (tourFrameRef.current !== null) cancelAnimationFrame(tourFrameRef.current)
    },
    [],
  )

  useEffect(() => {
    if (!request?.focus || tour || submitting) return
    const frame = requestAnimationFrame(() => {
      if (!disabled && (chat || owner || environment) && (!activeSurface || activeSurface === 'relaunch-announcement'))
        return
      textAreaRef?.current?.focus()
    })
    return () => cancelAnimationFrame(frame)
  }, [activeSurface, chat, disabled, environment, owner, request, submitting, textAreaRef, tour])

  useEffect(() => {
    if (submitting && tour) close()
  }, [close, submitting, tour])

  useEffect(() => {
    if (submitting && request) clearEnvironmentStripTourRequest()
  }, [request, submitting])

  useEffect(() => {
    if (!tour || activeSurface !== 'free-user-popover') return
    releaseRef.current?.()
    releaseRef.current = null
    onVisibilityChange(false)
    const frame = requestAnimationFrame(() => setTour(null))
    return () => cancelAnimationFrame(frame)
  }, [activeSurface, onVisibilityChange, tour])

  useEffect(() => {
    if (disabled || submitting || tour || dismissedRef.current || !scope) return
    if (!chatPickerReady) return
    if (!request && isNoticeDismissedForPage(ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE)) return
    if (!request && !ignoreDismiss && dismissal.data !== false) return
    const candidates: Array<[OnboardingAnchorId, HTMLButtonElement | null]> = [
      ['chat', chatPickerFailed ? null : chat],
      ...(!chatOwner ? [['owner', owner] as [OnboardingAnchorId, HTMLButtonElement | null]] : []),
      ['environment', environment],
    ]
    const steps = candidates
      .filter((entry): entry is [OnboardingAnchorId, HTMLButtonElement] => Boolean(entry[1]))
      .sort((a, b) => (a[1].compareDocumentPosition(b[1]) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
      .map(([id]) => id)
    const first = steps[0]
    if (!first) return
    const claimed =
      request && activeSurface === 'relaunch-announcement'
        ? handoverOnboardingSurface('relaunch-announcement', 'environment-strip-tour')
        : claimOnboardingSurface('environment-strip-tour')
    if (!claimed) return
    releaseRef.current = claimed
    const focusRequested = consumeEnvironmentStripTourRequest()?.focus ?? false
    tourFrameRef.current = requestAnimationFrame(() => {
      tourFrameRef.current = null
      setTour({steps, current: first, focusRequested, focusKey: 0})
    })
  }, [
    activeSurface,
    chat,
    chatPickerFailed,
    chatOwner,
    chatPickerReady,
    disabled,
    dismissal.data,
    environment,
    ignoreDismiss,
    owner,
    request,
    scope,
    submitting,
    tour,
  ])

  useEffect(() => {
    if (!tour || !scope) return
    const current = {chat, owner, environment}[tour.current]
    if (current && isVisibleOnboardingAnchor(current, scope)) return
    const remaining = tour.steps.filter(id => {
      const anchor = {chat, owner, environment}[id]
      return anchor && isVisibleOnboardingAnchor(anchor, scope)
    })
    const first = remaining[0]
    if (!first) {
      releaseRef.current?.()
      releaseRef.current = null
      tourFrameRef.current = requestAnimationFrame(() => {
        tourFrameRef.current = null
        setTour(null)
      })
      if (focusInTourRef.current || (tour.focusRequested && document.activeElement === document.body)) {
        textAreaRef?.current?.focus()
      }
      return
    }
    tourFrameRef.current = requestAnimationFrame(() => {
      tourFrameRef.current = null
      setTour(previous => previous && {...previous, steps: remaining, current: first})
    })
  }, [chat, environment, owner, scope, textAreaRef, tour])

  if (!tour || activeSurface !== 'environment-strip-tour' || submitting) return null
  const anchors = {chat, owner, environment}
  const steps = tour.steps.filter(id => {
    const anchor = anchors[id]
    return anchor && isVisibleOnboardingAnchor(anchor, scope)
  })
  const currentIndex = steps.indexOf(tour.current)
  const anchor = anchors[tour.current]
  const portalSlot = portalSlots[tour.current]
  if (!anchor || currentIndex < 0 || !portalSlot) return null
  const content = {
    chat: {
      title: 'Choose Chat or a repository',
      description: chatOwner
        ? 'Start a general chat for Personal or an organization, or select a repository to give Copilot project context.'
        : 'Start a general chat, or select a repository to give Copilot project context.',
    },
    owner: {
      title: 'Set the session scope',
      description: 'Choose Personal or an organization to set which repositories are available to your session.',
    },
    environment: {
      title: 'Work faster in the cloud',
      description: 'Sessions in Cloud now start much faster in secure, isolated sandboxes.',
    },
  }[tour.current]
  return (
    <CopilotOnboardingPopover
      anchor={anchor}
      scope={scope}
      escapeScope={getEscapeScope}
      portalSlot={portalSlot}
      textAreaRef={textAreaRef}
      onVisibilityChange={onVisibilityChange}
      onFocusChange={focused => {
        focusInTourRef.current = focused
      }}
      title={content.title}
      description={content.description}
      onClose={close}
      focusRequested={tour.focusRequested}
      focusKey={tour.focusKey}
      step={{
        index: currentIndex,
        count: steps.length,
        onPrevious:
          currentIndex > 0
            ? () => {
                const previousStep = steps[currentIndex - 1]
                if (previousStep)
                  setTour(
                    previous =>
                      previous && {
                        ...previous,
                        current: previousStep,
                        focusRequested: true,
                        focusKey: previous.focusKey + 1,
                      },
                  )
              }
            : undefined,
        onNext: () => {
          if (currentIndex === steps.length - 1) close()
          else {
            const nextStep = steps[currentIndex + 1]
            if (nextStep)
              setTour(
                previous =>
                  previous && {
                    ...previous,
                    current: nextStep,
                    focusRequested: true,
                    focusKey: previous.focusKey + 1,
                  },
              )
          }
        },
      }}
    />
  )
}
